import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, BookOpen, Heart, Users, Clock, Lightbulb, Target } from 'lucide-react';

export default function BibleStudyTips() {
  const navigate = useNavigate();

  return (
    <div 
      className="min-h-screen p-3 sm:p-4 md:p-6 relative"
      style={{
        backgroundImage: 'url(https://customer-assets.emergentagent.com/job_mega-church-game/artifacts/d1c9p3vy_FreeVector-Modern-City-Vector-Art.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#4169E1]/40 via-[#9370DB]/40 to-[#FFD700]/30"></div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <Button
          onClick={() => navigate('/start')}
          variant="ghost"
          className="mb-6 text-white hover:bg-white/20"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Game
        </Button>

        <Card className="bg-white/95 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 lg:p-12 shadow-2xl">
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 mb-6 sm:mb-8">
            <div className="p-2 sm:p-3 md:p-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl sm:rounded-2xl flex-shrink-0">
              <BookOpen className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-[#4169E1] to-[#9370DB] bg-clip-text text-transparent leading-tight">
              Bible Study Tips & Methods
            </h1>
          </div>

          <div className="prose prose-sm sm:prose-base max-w-none space-y-6 sm:space-y-8">
            {/* Introduction */}
            <section>
              <p className="text-lg text-gray-700 leading-relaxed">
                Studying the Bible is a rewarding journey that deepens your faith and understanding of God's Word. 
                Whether you're a beginner or have been studying for years, these proven methods and tips will help 
                you get more out of your Bible study time. The Mega Church Game is designed to reinforce your 
                biblical knowledge through engaging quizzes, but consistent personal study is the foundation of 
                spiritual growth.
              </p>
            </section>

            {/* Method 1: S.O.A.P. Method */}
            <section className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-6 h-6 text-blue-600" />
                <h2 className="text-3xl font-bold text-gray-800">The S.O.A.P. Method</h2>
              </div>
              <p className="text-gray-700 mb-4">
                The S.O.A.P. method is one of the most popular approaches to personal Bible study. It provides 
                a simple structure that helps you engage deeply with Scripture.
              </p>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <h3 className="text-xl font-semibold text-blue-600 mb-2">S - Scripture</h3>
                  <p className="text-gray-700">
                    Write out the Bible passage you're studying. This helps you slow down and notice details 
                    you might otherwise miss. Consider using multiple translations to gain different perspectives 
                    on the same verses.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <h3 className="text-xl font-semibold text-purple-600 mb-2">O - Observation</h3>
                  <p className="text-gray-700">
                    What do you observe in the passage? Ask questions like: Who is speaking? Who is the audience? 
                    What is the context? What are the key words or phrases? Look for repeated themes, commands, 
                    warnings, or promises.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <h3 className="text-xl font-semibold text-green-600 mb-2">A - Application</h3>
                  <p className="text-gray-700">
                    How does this passage apply to your life today? This is where transformation happens. Consider 
                    what God is teaching you and how you can put it into practice. Be specific about actions you 
                    can take this week.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <h3 className="text-xl font-semibold text-yellow-600 mb-2">P - Prayer</h3>
                  <p className="text-gray-700">
                    Turn your study into a conversation with God. Pray about what you've learned, ask for wisdom 
                    to apply it, and thank God for His Word. This closes the loop between studying and living 
                    out Scripture.
                  </p>
                </div>
              </div>
            </section>

            {/* Method 2: Inductive Bible Study */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Lightbulb className="w-6 h-6 text-yellow-600" />
                <h2 className="text-3xl font-bold text-gray-800">Inductive Bible Study</h2>
              </div>
              <p className="text-gray-700 mb-4">
                Inductive Bible Study is a deeper approach that involves three main steps: Observation, 
                Interpretation, and Application. This method helps you discover what the Bible says on its own 
                terms rather than imposing your own ideas onto the text.
              </p>
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-xl space-y-3">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">1. Observation: What does it say?</h3>
                  <p className="text-gray-700">
                    Read the passage multiple times. Mark key words, people, places, and events. Look for 
                    literary structure, repeated words, and connections between verses.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">2. Interpretation: What does it mean?</h3>
                  <p className="text-gray-700">
                    Understand the passage in its original context. Research the historical background, 
                    cultural setting, and author's intent. Use cross-references and study tools to gain clarity.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">3. Application: How should I respond?</h3>
                  <p className="text-gray-700">
                    Bridge the gap from "then" to "now." Ask how the timeless truths of Scripture apply to 
                    your modern life. Commit to specific, measurable actions.
                  </p>
                </div>
              </div>
            </section>

            {/* Essential Tips */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <Heart className="w-6 h-6 text-red-600" />
                <h2 className="text-3xl font-bold text-gray-800">Essential Bible Study Tips</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-red-50 to-pink-50 p-5 rounded-xl">
                  <Clock className="w-8 h-8 text-red-600 mb-3" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Be Consistent</h3>
                  <p className="text-gray-700">
                    Set aside a regular time each day for Bible study. Even 15-20 minutes daily is more 
                    effective than occasional longer sessions. Consistency builds spiritual disciplines 
                    and helps Scripture become part of your daily life.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl">
                  <BookOpen className="w-8 h-8 text-blue-600 mb-3" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Use Study Tools</h3>
                  <p className="text-gray-700">
                    Invest in quality study resources like a study Bible, concordance, and Bible dictionary. 
                    Online tools and apps can also enhance your study. However, remember that tools are 
                    supplements—the Bible itself should always be your primary source.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl">
                  <Users className="w-8 h-8 text-green-600 mb-3" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Study with Others</h3>
                  <p className="text-gray-700">
                    Join a Bible study group at your church or start one with friends. Community study 
                    provides accountability, different perspectives, and deeper insights. You'll be 
                    encouraged by others' faith journeys and can encourage them in return.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-5 rounded-xl">
                  <Target className="w-8 h-8 text-purple-600 mb-3" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Start Where You Are</h3>
                  <p className="text-gray-700">
                    Don't feel pressured to read the entire Bible immediately. Start with the Gospels 
                    (Matthew, Mark, Luke, John) to learn about Jesus, or begin with Psalms for worship 
                    and prayer. Build gradually from there.
                  </p>
                </div>
              </div>
            </section>

            {/* Practical Study Plans */}
            <section className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-2xl">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Practical Study Plans</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-indigo-600 mb-2">Beginner: Gospel Reading Plan</h3>
                  <p className="text-gray-700">
                    Start with the Gospel of John (21 chapters). Read one chapter per day with the S.O.A.P. 
                    method. This gives you a solid foundation in Jesus' life and teachings in just three weeks.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-blue-600 mb-2">Intermediate: Topical Study</h3>
                  <p className="text-gray-700">
                    Choose a topic (like faith, love, prayer, or wisdom) and study what the Bible says about 
                    it. Use a concordance to find relevant verses throughout Scripture. This deepens your 
                    understanding of specific themes.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-purple-600 mb-2">Advanced: Book-by-Book Study</h3>
                  <p className="text-gray-700">
                    Study entire books of the Bible in depth. Start with shorter books like Philippians, 
                    James, or 1 Peter. Read the whole book first for context, then study it section by section 
                    using the inductive method.
                  </p>
                </div>
              </div>
            </section>

            {/* Connection to the Game */}
            <section className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6 rounded-2xl border-2 border-yellow-300">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">How Mega Church Game Helps Your Study</h2>
              <p className="text-gray-700 mb-4">
                The Mega Church Game is designed to complement your personal Bible study by:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">✓</span>
                  <span>
                    <strong>Reinforcing key biblical facts and stories</strong> through engaging quizzes 
                    across six difficulty levels
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">✓</span>
                  <span>
                    <strong>Testing your knowledge</strong> to identify areas where you might need more study
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">✓</span>
                  <span>
                    <strong>Making Bible learning fun and interactive</strong> through gamification and competition
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-600 font-bold">✓</span>
                  <span>
                    <strong>Encouraging regular engagement</strong> with Scripture through progressive levels
                  </span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                Use the game as a supplement to—not a replacement for—regular Bible study. The questions 
                are designed to spark curiosity and drive you back to Scripture for deeper understanding.
              </p>
            </section>

            {/* Conclusion */}
            <section>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Start Your Journey Today</h2>
              <p className="text-gray-700 mb-4">
                Bible study is a lifelong journey of discovery and growth. The methods and tips shared here 
                are starting points—feel free to adapt them to your learning style and season of life. The 
                most important thing is to start where you are and be consistent.
              </p>
              <p className="text-gray-700 mb-4">
                Remember that the goal of Bible study isn't just to gain knowledge, but to know God more 
                deeply and be transformed by His Word. As you study, pray for the Holy Spirit to illuminate 
                the text and apply it to your heart. Stay humble, remain teachable, and let Scripture shape 
                your life.
              </p>
              <p className="text-gray-700 font-semibold">
                "Your word is a lamp to my feet and a light to my path." - Psalm 119:105
              </p>
            </section>

            {/* Call to Action */}
            <div className="text-center pt-8 border-t-2 border-gray-200">
              <Button
                onClick={() => navigate('/level-select')}
                className="bg-gradient-to-r from-[#4169E1] to-[#9370DB] text-white px-8 py-6 text-lg rounded-2xl hover:shadow-2xl transition-all"
              >
                Test Your Knowledge in the Game
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
